/** @jsxImportSource @emotion/react */

import {
  CalciteInlineEditable,
  CalciteInput,
} from "@esri/calcite-components-react";
import { Button } from "components/Button";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import { useContext, useState } from "react";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import { fieldsEqual, formatPhoneNumber } from "utils/helpers";

const discrepClient = new RecordService("discrep");

export const Lvf = ({ data, discrep }) => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const uncheckedFields = data.lvf.unchecked?.split(/\s+/) || [];

  const [lvfData, setLvfData] = useState([
    {
      key: "House Number Prefix",
      submitted: data.lvf.ORG_HNP,
      requested: data.lvf.EDIT_HNP,
      current: data.ali && data.ali.HNP,
      name: "hnp",
      max: 1,
    },
    {
      key: "House Number",
      submitted: data.lvf.ORG_HNO,
      requested: data.lvf.EDIT_HNO,
      current: data.ali && data.ali.HNO,
      name: "hno",
      max: 10,
    },
    {
      key: "House Number Suffix",
      submitted: data.lvf.ORG_HNS,
      requested: data.lvf.EDIT_HNS,
      current: data.ali && data.ali.HNS,
      name: "hns",
      max: 4,
    },
    {
      key: "Prefix Directional",
      submitted: data.lvf.ORG_PRD,
      requested: data.lvf.EDIT_PRD,
      current: data.ali && data.ali.PRD,
      name: "prd",
      max: 2,
    },
    {
      key: "Street Name",
      submitted: data.lvf.ORG_STN,
      requested: data.lvf.EDIT_STN,
      current: data.ali && data.ali.STN,
      name: "stn",
      max: 60,
    },
    {
      key: "Street Suffix",
      submitted: data.lvf.ORG_STS,
      requested: data.lvf.EDIT_STS,
      current: data.ali && data.ali.STS,
      name: "sts",
      max: 4,
    },
    {
      key: "Post Directional",
      submitted: data.lvf.ORG_POD,
      requested: data.lvf.EDIT_POD,
      current: data.ali && data.ali.POD,
      name: "pod",
      max: 2,
    },
    {
      key: "MSAG Community Name",
      submitted: data.lvf.ORG_MCN,
      requested: data.lvf.EDIT_MCN,
      current: data.ali && data.ali.MCN,
      name: "mcn",
      max: 32,
    },
    {
      key: "County",
      submitted: data.lvf.ORG_lecCOI,
      requested: data.lvf.EDIT_lecCOI,
      current: data.ali && data.ali.lecCOI,
      name: "lecCoi",
      max: 4,
    },
    {
      key: "State",
      submitted: data.lvf.ORG_STA,
      requested: data.lvf.EDIT_STA,
      current: data.ali && data.ali.STA,
      name: "sta",
      max: 2,
    },
    {
      key: "Location",
      submitted: data.lvf.ORG_LOC,
      requested: data.lvf.EDIT_LOC,
      current: data.ali && data.ali.LOC,
      name: "loc",
      max: 60,
    },
    {
      key: "Timestamp",
      submitted: discrep.submitted,
      requested: data.lvf.EDIT_requested,
      current: data.ali && data.ali.last_modified,
    },
  ]);

  const [lvfRequest, setLvfRequest] = useState({
    EDIT_HNP: data.lvf.EDIT_HNP || "",
    EDIT_HNO: data.lvf.EDIT_HNO || "",
    EDIT_HNS: data.lvf.EDIT_HNS || "",
    EDIT_PRD: data.lvf.EDIT_PRD || "",
    EDIT_STN: data.lvf.EDIT_STN || "",
    EDIT_STS: data.lvf.EDIT_STS || "",
    EDIT_POD: data.lvf.EDIT_POD || "",
    EDIT_MCN: data.lvf.EDIT_MCN || "",
    EDIT_lecCOI: data.lvf.EDIT_lecCOI || "",
    EDIT_STA: data.lvf.EDIT_STA || "",
    EDIT_LOC: data.lvf.EDIT_LOC || "",
  });

  const fieldMapping = {
    hnp: "EDIT_HNP",
    hno: "EDIT_HNO",
    hns: "EDIT_HNS",
    prd: "EDIT_PRD",
    stn: "EDIT_STN",
    sts: "EDIT_STS",
    pod: "EDIT_POD",
    mcn: "EDIT_MCN",
    lecCoi: "EDIT_lecCOI",
    sta: "EDIT_STA",
    loc: "EDIT_LOC",
  };

  const submitChanges = async () => {
    try {
      const update = await discrepClient.updateDiscrepancy(
        "discrepancyLvf",
        {
          id: discrep.id,
          table_name: "lvf",
          Criteria: [
            {
              field: "status",
              value: discrep?.status, // keep current status
            },
          ],
          //   {
          //     field: "question",
          //     value:
          //       typeof discrep?.question === "string"
          //         ? discrep.question
          //         : String(discrep?.question), // keep current question value, ensuring it's a string
          //   },
          // ],
        },
        {
          Criteria: Object.entries(lvfRequest)
            .filter(([key, value]) => {
              // if the value is different from lvf.row.requested, include it in the update
              const lvfField = fieldMapping[key];
              const initialValue = data.lvf[lvfField];
              return value !== initialValue;
            })
            .map(([key, value]) => ({
              field: key,
              value,
            })),
        },
        discrep.question,
      );
      console.log(update);
      addNotification({
        title: "Success",
        message: "Successfully updated LVF requested values",
        type: NOTIFICATION_TYPE.SUCCESS,
      });
      // refresh page to show updated values
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      addNotification({
        title: "Error",
        message: `Unable to update LVF requested values`,
        type: NOTIFICATION_TYPE.ERROR,
      });
    }
  };

  function getCellBackgroundColor(row) {
    // Base styles
    const baseStyles = tw`border-r border-gray-400 px-2 w-1/4`;

    // Check if field has changes from submitted/current
    const hasRequestChanges =
      row.key !== "Timestamp" &&
      row.name &&
      !uncheckedFields.includes(row.name) &&
      (!fieldsEqual(row.requested, row.submitted) ||
        !fieldsEqual(row.requested, row.current));

    // Check if field has been modified from original
    const hasModifications =
      row.key !== "Timestamp" &&
      row.name &&
      (() => {
        const lvfField = fieldMapping[row.name];
        const initialValue = data.lvf[lvfField];
        const currentLvfValue = lvfRequest[lvfField];
        return lvfField && !fieldsEqual(initialValue, currentLvfValue);
      })();

    // Return combined styles
    return [
      baseStyles,
      hasRequestChanges && tw`bg-yellow-600`,
      hasModifications && tw`bg-orange-500`,
    ];
  }

  return (
    <div css={[tw`col-span-2 `]}>
      <div css={[tw`flex justify-between items-center mb-4`]}>
        <h3>
          LVF test of {formatPhoneNumber(discrep?.ANI)}
          {data?.ali?.NAM && ` (${data.ali.NAM})`}
        </h3>

        {user?.functions?.includes("foresite:discrep:updateByJurisdiction") && (
          <div css={[tw`flex gap-2`]}>
            <Button onClick={submitChanges} kind="brand">
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div
        css={[
          tw`col-span-2 overflow-hidden  border border-gray-400 rounded-md`,
        ]}
      >
        <table css={[tw`w-full`]}>
          <thead css={[tw`bg-gray-200 border-b border-gray-400`]}>
            <tr css={[tw`text-left`]}>
              <th css={[tw`border-r border-gray-400 p-2`]}></th>

              <th css={[tw`font-semibold border-r border-gray-400 p-2`]}>
                Submitted to LVF
              </th>
              <th css={[tw`border-r border-gray-400 font-semibold p-2`]}>
                Requested
              </th>
              <th css={[tw`font-semibold p-2`]}>Current</th>
            </tr>
          </thead>
          <tbody>
            {lvfData.map((row, index) => {
              return (
                <tr key={row.key} css={[tw`border-b border-gray-300`]}>
                  <td
                    css={[tw`font-semibold border-r border-gray-400 p-2 w-1/4`]}
                  >
                    {row.key}
                  </td>
                  <td
                    css={[
                      tw`border-r border-gray-400 p-2 w-1/4`,
                      row.name &&
                        uncheckedFields.includes(row.name) &&
                        tw`bg-gray-300`,
                      row.name &&
                        !uncheckedFields.includes(row.name) &&
                        data.lvf.invalid?.includes(row.name) &&
                        tw`bg-red-500`,
                      row.name &&
                        !uncheckedFields.includes(row.name) &&
                        data.lvf.valid?.includes(row.name) &&
                        tw`bg-green-500`,
                    ]}
                  >
                    {row.submitted}
                  </td>
                  <td css={getCellBackgroundColor(row)}>
                    {user?.functions?.includes(
                      "foresite:discrep:updateByJurisdiction",
                    ) && row.key !== "Timestamp" ? (
                      <CalciteInlineEditable
                        style={{
                          "--calcite-inline-editable-background-color":
                            "transparent",
                          "--calcite-inline-editable-background-color-hover":
                            "transparent",
                        }}
                        onCalciteInlineEditableEditCancel={() => {
                          setLvfData((prevData) => {
                            const newData = [...prevData];
                            newData[index] = {
                              ...newData[index],
                              requested:
                                (row.name &&
                                  data.lvf[fieldMapping[row.name]]) ||
                                "",
                            };
                            return newData;
                          });
                          // Reset lvfRequest to original value

                          const lvfField = row.name && fieldMapping[row.name];
                          if (lvfField) {
                            setLvfRequest((prev) => ({
                              ...prev,
                              [lvfField]: data.lvf[lvfField],
                            }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            (e.target as HTMLElement).blur();
                          }
                        }}
                      >
                        <CalciteInput
                          value={row.requested}
                          scale="l"
                          onCalciteInputInput={(e) => {
                            if (row.max) {
                              const inputValue = e.target.value;
                              if (inputValue.length > row.max) {
                                e.target.value = inputValue.slice(0, row.max);
                              }
                            }

                            const newValue = e.target.value;
                            setLvfData((prevData) => {
                              const newData = [...prevData];
                              newData[index] = {
                                ...newData[index],
                                requested: newValue,
                              };
                              return newData;
                            });
                            const lvfField = row.name && fieldMapping[row.name];
                            if (lvfField) {
                              setLvfRequest((prev) => ({
                                ...prev,
                                [lvfField]: newValue,
                              }));
                            }
                          }}
                        />
                      </CalciteInlineEditable>
                    ) : (
                      row.requested
                    )}
                  </td>
                  <td
                    css={[
                      tw`p-2 w-1/4`,
                      row.key !== "Timestamp" &&
                        row.name &&
                        !uncheckedFields.includes(row.name) &&
                        data.ali !== null &&
                        !fieldsEqual(row.requested, row.current) &&
                        tw`bg-yellow-600`,
                    ]}
                  >
                    {row.current}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Lvf;
